// src/components/expert/ExpertAssignmentView.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  format,
  addMonths,
  differenceInDays,
  parseISO,
  startOfMonth,
  subMonths,
  endOfMonth,
} from "date-fns";
import expertService from "../../services/expertService";
import authService from "../../services/authService";
import Modal, { ModalSecondaryButton } from "../common/Modal";
import { useAuth } from "../../contexts/AuthContext";
import { coversProfession } from "../../utils/professionScope";

const PROFESSIONS = [
  { value: "ALL", label: "All Professions" },
  { value: "MEDIAMATIK", label: "Mediamatik" },
  { value: "INFORMATIK", label: "Informatik" },
  { value: "KV", label: "KV" },
  { value: "KUNDENDIALOG", label: "Kundendialog" },
  { value: "DETAILHANDEL", label: "Detailhandel" },
];

const PROFESSION_COLORS = {
  MEDIAMATIK: "bg-purple-600",
  INFORMATIK: "bg-blue-600",
  KV: "bg-gray-500",
  KUNDENDIALOG: "bg-green-600",
  DETAILHANDEL: "bg-orange-500",
  DEFAULT: "bg-slate-500",
};

const getInstructorGroupKey = (assignment) =>
  `${assignment.instructorId || "unknown"}::${assignment.instructorName || "Unknown"}`;

const ExpertAssignmentView = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState([]);
  const [unassignedLearners, setUnassignedLearners] = useState([]);
  const [filters, setFilters] = useState({
    professionType: user?.professionType || "ALL",
    lehrjahr: "",
    search: "",
    showHistory: false,
  });
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [error, setError] = useState(null);
  // Timeline Config: Dynamic rolling window (1 month past, 11 months future)
  const today = new Date();
  const timelineStart = startOfMonth(subMonths(today, 1));
  const timelineEnd = endOfMonth(addMonths(timelineStart, 11));
  const totalDays = differenceInDays(timelineEnd, timelineStart) + 1;

  const months = useMemo(() => {
    const m = [];
    for (let i = 0; i < 12; i++) m.push(addMonths(timelineStart, i));
    return m;
  }, [timelineStart]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filters.professionType !== "ALL")
        params.professionType = filters.professionType;
      if (filters.lehrjahr) params.lehrjahr = filters.lehrjahr;

      const data = await expertService.getTimelineData(params);
      
      if (data.assignments) {
        setAssignments(data.assignments);
        // Map unassigned learners to a compatible structure
        const mappedUnassigned = (data.unassignedLearners || []).map(u => ({
          id: `unassigned-${u.id}`,
          studentId: u.id,
          studentName: u.name,
          lehrjahr: u.lehrjahr,
          professionType: u.professionType,
          active: false,
          isUnassigned: true,
          instructorName: "Unassigned",
          startAt: new Date().toISOString() // Placeholder
        }));
        setUnassignedLearners(mappedUnassigned);
      } else {
        // Fallback for old API response
        setAssignments(Array.isArray(data) ? data : []);
        setUnassignedLearners([]);
      }
    } catch (e) {
      console.error("Failed to fetch timeline data", e);
      setError("Failed to load timeline data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [filters.professionType, filters.lehrjahr]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredAssignments = useMemo(() => {
    return assignments.filter((a) => {
      const searchMatch =
        !filters.search ||
        a.studentName.toLowerCase().includes(filters.search.toLowerCase()) ||
        a.instructorName.toLowerCase().includes(filters.search.toLowerCase()) ||
        (a.department || "").toLowerCase().includes(filters.search.toLowerCase());

      const historyMatch = filters.showHistory || a.active;
      const lehrjahrMatch =
        !filters.lehrjahr || a.lehrjahr === filters.lehrjahr;

      return searchMatch && historyMatch && lehrjahrMatch;
    });
  }, [assignments, filters.search, filters.showHistory, filters.lehrjahr]);

  const filteredUnassigned = useMemo(() => {
    return unassignedLearners.filter((u) => {
       const searchMatch = !filters.search || 
         u.studentName.toLowerCase().includes(filters.search.toLowerCase());
       
       const lehrjahrMatch = !filters.lehrjahr || u.lehrjahr === parseInt(filters.lehrjahr, 10);
       
       // Note: professionType is already filtered by backend
       return searchMatch && lehrjahrMatch;
    });
  }, [unassignedLearners, filters.search, filters.lehrjahr]);

  const groupedData = useMemo(() => {
    const groups = {};
    
    // Group Assignments
    filteredAssignments.forEach((a) => {
      const lj = a.lehrjahr || 0;
      if (!groups[lj]) groups[lj] = { instructors: {}, unassigned: [] };
      const groupKey = getInstructorGroupKey(a);
      if (!groups[lj].instructors[groupKey]) {
        groups[lj].instructors[groupKey] = {
          instructorName: a.instructorName,
          department: a.department,
          assignments: [],
        };
      }
      groups[lj].instructors[groupKey].assignments.push(a);
    });

    // Group Unassigned
    filteredUnassigned.forEach((u) => {
      const lj = u.lehrjahr || 0;
      if (!groups[lj]) groups[lj] = { instructors: {}, unassigned: [] };
      groups[lj].unassigned.push(u);
    });

    return groups;
  }, [filteredAssignments, filteredUnassigned]);

  // Shared props for child components
  const timelineProps = {
    timelineStart,
    timelineEnd,
    totalDays,
    months,
    today,
  };

  return (
    <div className="min-h-screen bg-green-cloud">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="card-brand-elevated p-8 bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-grey-dusk flex items-center mb-2">
                  <i className="fas fa-sitemap text-sunrise-red mr-4"></i>{" "}
                  Instructor Timeline
                </h1>
                <p className="text-slate-cloud text-sm mt-1">
                  {format(timelineStart, "MMM yyyy")} –{" "}
                  {format(timelineEnd, "MMM yyyy")}
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Alerts */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex justify-between items-center text-sm">
            <span>{error}</span>
            <button onClick={() => setError(null)}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}
        {/* Filters Toolbar */}
        <div className="bg-white border border-pale-dawn rounded-lg px-4 py-3 mb-5 shadow-sm">
          <div className="flex flex-wrap items-center gap-3">
            {/* Label */}
            <div className="flex items-center gap-2 text-xs font-semibold text-grey-dusk whitespace-nowrap">
              <i className="fas fa-filter text-sunrise-red text-xs"></i>
              Filters
            </div>

            {/* Profession */}
            <select
              className="h-8 px-2 text-xs border border-pale-dawn rounded-md bg-white text-grey-dusk focus:ring-1 focus:ring-sunrise-red focus:border-sunrise-red"
              value={filters.professionType}
              onChange={(e) =>
                setFilters({ ...filters, professionType: e.target.value })
              }
            >
              {PROFESSIONS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>

            {/* Lehrjahr */}
            <select
              className="h-8 px-2 text-xs border border-pale-dawn rounded-md bg-white text-grey-dusk focus:ring-1 focus:ring-sunrise-red focus:border-sunrise-red"
              value={filters.lehrjahr}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  lehrjahr: e.target.value ? parseInt(e.target.value, 10) : "",
                })
              }
            >
              <option value="">All Years</option>
              <option value="1">1. Lehrjahr</option>
              <option value="2">2. Lehrjahr</option>
              <option value="3">3. Lehrjahr</option>
              <option value="4">4. Lehrjahr</option>
            </select>

            {/* Search */}
            <div className="relative flex-1 min-w-[160px] max-w-[260px]">
              <i className="fas fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
              <input
                type="text"
                placeholder="Search..."
                className="h-8 w-full pl-7 pr-2 text-xs border border-pale-dawn rounded-md focus:ring-1 focus:ring-sunrise-red focus:border-sunrise-red"
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
              />
            </div>

            {/* Divider */}
            <div className="hidden md:block h-5 w-px bg-pale-dawn mx-1"></div>

            {/* History toggle */}
            <label className="flex items-center gap-1.5 text-xs text-grey-dusk cursor-pointer whitespace-nowrap">
              <input
                type="checkbox"
                checked={filters.showHistory}
                onChange={(e) =>
                  setFilters({ ...filters, showHistory: e.target.checked })
                }
                className="rounded border-gray-300 text-sunrise-red focus:ring-sunrise-red"
              />
              History
            </label>
          </div>
        </div>

        {/* Main Timeline Content */}
        {loading && assignments.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            <i className="fas fa-circle-notch fa-spin text-3xl mb-3 text-sunrise-red"></i>
            <p>Loading timeline...</p>
          </div>
        ) : (
          <div className="bg-white border border-pale-dawn rounded-xl shadow-brand-sm overflow-hidden flex flex-col">
            <div className="overflow-x-auto">
              <div className="min-w-[1000px] inline-block w-full align-middle">
                {/* Timeline Header */}
                <TimelineHeader {...timelineProps} />

                {/* Body Content */}
                <div className="bg-slate-50/50 min-h-[400px]">
                  {Object.keys(groupedData).length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                      <i className="fas fa-search text-4xl mb-3 opacity-20"></i>
                      <p>No assignments found matching your filters.</p>
                    </div>
                  ) : (
                    <div className="p-4 space-y-6">
                      {Object.keys(groupedData)
                        .sort((a, b) => Number(a) - Number(b))
                        .map((lj) => (
                          <LehrjahrSection
                            key={lj}
                            lehrjahr={lj}
                            instructorsGroup={groupedData[lj].instructors || {}}
                            unassignedGroup={groupedData[lj].unassigned || []}
                            timelineProps={timelineProps}
                            onSelect={(a) => {
                              setSelectedAssignment(a);
                              setIsModalOpen(true);
                            }}
                          />
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {isModalOpen && selectedAssignment && (
        <AssignmentModal
          assignment={selectedAssignment}
          onClose={() => setIsModalOpen(false)}
          onUpdate={() => {
            setIsModalOpen(false);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

// --- Subcomponents ---

const TimelineHeader = ({
  months,
  today,
  timelineStart,
  totalDays,
  timelineEnd,
}) => {
  return (
    <div className="grid grid-cols-[240px_1fr] bg-white border-b border-gray-200 sticky top-0 z-20 shadow-sm">
      <div className="sticky left-0 z-30 p-3 text-xs font-bold text-gray-500 tracking-wider border-r border-gray-200 flex items-center bg-gray-50 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
        Instructor
      </div>
      <div className="relative flex h-10 divide-x divide-gray-100">
        {months.map((m, i) => (
          <div
            key={i}
            className="flex-1 flex items-center justify-center text-[10px] font-semibold text-gray-500 bg-white"
          >
            {format(m, "MMM")}
          </div>
        ))}
        {/* Today Marker */}
        {today >= timelineStart && today <= timelineEnd && (
          <div
            className="absolute top-0 bottom-0 border-l-2 border-red-500/50 z-30 pointer-events-none"
            style={{
              left: `${(differenceInDays(today, timelineStart) / totalDays) * 100}%`,
            }}
            title="Today"
          >
            <div className="w-1.5 h-1.5 bg-red-500 rounded-full -ml-[4px] -mt-[1px]"></div>
          </div>
        )}
      </div>
    </div>
  );
};

const LehrjahrSection = ({
  lehrjahr,
  instructorsGroup,
  unassignedGroup,
  timelineProps,
  onSelect,
}) => {
  const sortedInstructors = useMemo(
    () => Object.keys(instructorsGroup).sort(),
    [instructorsGroup],
  );

  return (
    <div className="mb-4">
      <div className="sticky left-0 z-10 mb-2 flex items-center gap-2">
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-grey-dusk border border-slate-200 shadow-sm">
          {lehrjahr}. Lehrjahr
          <span className="ml-2 bg-white text-gray-500 py-0.5 px-1.5 rounded-md text-[10px]">
            {sortedInstructors.length} instructors
          </span>
        </span>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden divide-y divide-gray-100">
        {/* Unassigned Block */}
        {unassignedGroup && unassignedGroup.length > 0 && (
          <UnassignedLearnersRow 
            learners={unassignedGroup} 
            timelineProps={timelineProps}
            onSelect={onSelect}
          />
        )}
        
        {/* Empty state if nothing at all */}
        {sortedInstructors.length === 0 && (!unassignedGroup || unassignedGroup.length === 0) && (
             <div className="p-4 text-center text-gray-400 text-sm italic">No assignments found</div>
        )}

        {/* Assigned Instructors */}
        {sortedInstructors.map((instructor) => (
          <InstructorTimelineRow
            key={instructor}
            instructorName={instructorsGroup[instructor].instructorName}
            department={instructorsGroup[instructor].department}
            assignments={instructorsGroup[instructor].assignments}
            timelineProps={timelineProps}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
};


const InstructorTimelineRow = ({
  instructorName,
  department,
  assignments,
  timelineProps,
  onSelect,
}) => {
  // Sort assignments: active first, then date
  const sortedAssignments = useMemo(() => {
    return [...assignments].sort((a, b) => {
      if (a.active !== b.active) return a.active ? -1 : 1;
      return new Date(a.startAt) - new Date(b.startAt);
    });
  }, [assignments]);

  // Determine profession from first assignment if available (usually consistent per instructor in this view context)
  const profession = assignments[0]?.professionType;
  // Get profession count summary if needed, but simple count is fine.

  return (
    <div className="grid grid-cols-[240px_1fr] bg-white hover:bg-slate-50/80 transition-colors group min-h-[50px]">
      {/* Left Column: Instructor Info */}
      <div className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 transition-colors p-3 border-r border-gray-200 flex flex-col justify-center shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
        <div
          className="font-semibold text-sm text-grey-dusk truncate"
          title={instructorName}
        >
          {instructorName}
        </div>
        {department && (
          <div
            className="text-xs text-slate-500 truncate mt-1"
            title={department}
          >
            {department}
          </div>
        )}
        <div className="flex items-center gap-2 mt-1">
          {profession && (
            <span
              className={`w-2 h-2 rounded-full ${PROFESSION_COLORS[profession] || "bg-gray-400"}`}
            ></span>
          )}
          <span className="text-xs text-slate-500">
            {assignments.length} apprentice{assignments.length !== 1 && "s"}
          </span>
        </div>
      </div>

      {/* Right Column: Timeline */}
      <div className="relative py-2 pl-0.5 pr-0.5">
        {/* Background Grid Lines */}
        <div className="absolute inset-0 flex divide-x divide-gray-100/60 pointer-events-none">
          {timelineProps.months.map((_, i) => (
            <div key={i} className="flex-1 h-full hover:bg-slate-50/10"></div>
          ))}
        </div>
        {timelineProps.today >= timelineProps.timelineStart &&
          timelineProps.today <= timelineProps.timelineEnd && (
            <div
              className="absolute top-0 bottom-0 w-px bg-red-400/20 z-0 pointer-events-none"
              style={{
                left: `${(differenceInDays(timelineProps.today, timelineProps.timelineStart) / timelineProps.totalDays) * 100}%`,
              }}
            />
          )}

        <div className="relative z-10 flex flex-col gap-1.5 min-h-[32px] justify-center">
          {sortedAssignments.map((assignment) => (
            <LearnerBar
              key={assignment.id}
              assignment={assignment}
              timelineProps={timelineProps}
              onClick={() => onSelect(assignment)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const LearnerBar = ({ assignment, timelineProps, onClick }) => {
  const { timelineStart, timelineEnd, totalDays } = timelineProps;

  const getStyles = () => {
    const start = parseISO(assignment.startAt);
    let end = assignment.endAt ? parseISO(assignment.endAt) : null;

    // Logic from original component
    if (!end && assignment.expirationDate) {
      end = parseISO(assignment.expirationDate);
    }

    const viewStart = start < timelineStart ? timelineStart : start;
    let viewEnd = end;
    if (!end) viewEnd = timelineEnd;
    else if (end > timelineEnd) viewEnd = timelineEnd;

    if (end && end < viewEnd) {
      viewEnd = end;
    }

    if (viewStart > timelineEnd || (end && end < timelineStart)) {
      return { display: "none" };
    }

    const offsetDays = differenceInDays(viewStart, timelineStart);
    const duration = differenceInDays(viewEnd, viewStart);

    const leftPct = (offsetDays / totalDays) * 100;
    const widthPct = (Math.max(duration, 1) / totalDays) * 100;

    return {
      left: `${Math.max(0, leftPct)}%`,
      width: `${Math.min(100 - Math.max(0, leftPct), widthPct)}%`,
    };
  };

  const styles = getStyles();
  if (styles.display === "none") return null;

  const colorClass =
    PROFESSION_COLORS[assignment.professionType] || PROFESSION_COLORS.DEFAULT;

  return (
    <div
      className={`group/bar relative h-7 rounded text-[11px] text-white px-2 
                   flex items-center cursor-pointer hover:brightness-110 
                   transition-all shadow-sm
                   ${colorClass} ${!assignment.active ? "opacity-50 grayscale-[0.3]" : ""}
                   `}
      style={styles}
      onClick={onClick}
    >
      <div className="truncate font-medium flex items-center w-full">
        <span className="w-1.5 h-1.5 bg-white/40 rounded-full mr-1.5 shrink-0 group-hover/bar:bg-white transition-colors"></span>
        {assignment.studentName}
      </div>

      {/* Tooltip */}
      <div className="absolute opacity-0 group-hover/bar:opacity-100 bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-[10px] rounded pointer-events-none whitespace-nowrap z-50 transition-opacity">
        {assignment.studentName} •{" "}
        {format(parseISO(assignment.startAt), "dd.MM.yy")} -{" "}
        {assignment.endAt
          ? format(parseISO(assignment.endAt), "dd.MM.yy")
          : assignment.expirationDate
          ? format(parseISO(assignment.expirationDate), "dd.MM.yy")
          : "End not defined"}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
      </div>
    </div>
  );
};

const UnassignedLearnersRow = ({ learners, timelineProps, onSelect }) => {
  const { timelineStart, timelineEnd, totalDays, today, months } = timelineProps;
  const { user } = useAuth();
  const canAssign = user?.roles?.some(r => 
    r.name === 'ADMIN' || 
    r.name === 'ROLE_ADMIN' || 
    r.name === 'BERUFSBILDNER' || 
    r.name === 'ROLE_BERUFSBILDNER'
  );

  return (
    <div className="grid grid-cols-[240px_1fr] bg-orange-50/30 border-b border-orange-100 group min-h-[50px] mb-2 rounded-lg border-l-4 border-l-orange-300">
      {/* Left Column */}
      <div className="sticky left-0 z-10 bg-orange-50/30 p-3 border-r border-orange-100 flex flex-col justify-center">
        <div className="font-semibold text-sm text-gray-600 flex items-center">
            <i className="fas fa-user-plus mr-2 text-orange-400"></i>
            Unassigned
        </div>
        <div className="text-xs text-slate-500 mt-0.5 ml-6">
            {learners.length} apprentices need assignments
        </div>
      </div>

      {/* Right Column: Timeline Area */}
      <div className="relative py-2 pl-0.5 pr-0.5 bg-orange-50/10">
         {/* Background Grid Lines */}
         <div className="absolute inset-0 flex divide-x divide-gray-200/40 pointer-events-none">
          {months.map((_, i) => (
            <div key={i} className="flex-1 h-full hover:bg-white/40"></div>
          ))}
        </div>
        
        {/* Today Marker */}
        {today >= timelineStart && today <= timelineEnd && (
            <div
              className="absolute top-0 bottom-0 w-px bg-red-400/20 z-0 pointer-events-none"
              style={{
                left: `${(differenceInDays(today, timelineStart) / totalDays) * 100}%`,
              }}
            />
        )}

        <div className="relative z-10 flex flex-col gap-1.5 min-h-[32px] justify-center px-1">
          {learners.map((learner) => (
             <div 
                key={learner.id}
                onClick={() => canAssign && onSelect(learner)}
                className={`relative h-7 rounded border border-dashed border-gray-400 bg-white 
                  ${canAssign ? 'hover:bg-orange-50 hover:border-orange-400 cursor-pointer' : 'cursor-default opacity-80'}
                  text-[11px] text-gray-600 px-2 flex items-center transition-all shadow-sm w-full max-w-xl group/unassigned`}
             >
                <div className="truncate font-medium flex items-center w-full">
                    <span className="w-1.5 h-1.5 bg-gray-300 group-hover/unassigned:bg-orange-400 rounded-full mr-2 shrink-0 transition-colors"></span>
                    {learner.studentName}
                    {canAssign && (
                        <div className="ml-auto opacity-0 group-hover/unassigned:opacity-100 transition-opacity flex items-center bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-[10px] font-bold">
                            <i className="fas fa-plus mr-1"></i> Assign
                        </div>
                    )}
                </div>
             </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const AssignmentModal = ({ assignment, onClose, onUpdate }) => {
  const isNewAssignment = assignment.isUnassigned;

  const [expirationDate, setExpirationDate] = useState(
    assignment.expirationDate ? assignment.expirationDate : "",
  );

  const [isReassigning, setIsReassigning] = useState(isNewAssignment);
  const [newInstructorId, setNewInstructorId] = useState("");
  const [reassignDate, setReassignDate] = useState(
    format(new Date(), "yyyy-MM-dd"),
  );

  // State for fetching instructors
  const [instructors, setInstructors] = useState([]);
  const [loadingInstructors, setLoadingInstructors] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Fetch instructors when reassign mode is active
  useEffect(() => {
    if (isReassigning && instructors.length === 0) {
      setLoadingInstructors(true);
      authService
        .getAllUsers()
        .then((response) => {
          if (response.success && Array.isArray(response.users)) {
            const relevant = response.users.filter((u) => {
              const hasRole = u.roles?.some(
                (r) =>
                  r.name === "PRACTICE_INSTRUCTOR" ||
                  r.name === "PRAXISBILDNER" ||
                   r.name === "PRACTICE INSTRUCTOR" // Safety check
              );
              if (!hasRole) return false;

              // If the assignment has a profession, keep the instructors who
              // are responsible for it (a PI may cover several professions).
              if (assignment.professionType) {
                return coversProfession(u, assignment.professionType);
              }
              return true;
            });
            setInstructors(relevant);
          }
        })
        .finally(() => setLoadingInstructors(false));
    }
  }, [isReassigning, instructors.length, assignment.professionType]);

  const handleUpdateExpiration = async () => {
    setSaving(true);
    setError(null);
    try {
      await expertService.updateExpirationDate(assignment.id, expirationDate);
      onUpdate();
    } catch (e) {
      setError("Error updating planned end date. Please try again.");
      setSaving(false);
    }
  };

  const handleReassign = async () => {
    setSaving(true);
    setError(null);
    try {
      await expertService.reassignStudent(
        assignment.studentId,
        newInstructorId === "UNASSIGN" ? null : newInstructorId,
        reassignDate,
        expirationDate,
      );
      onUpdate();
    } catch (e) {
      setError("Error assigning learner. Please check inputs and try again.");
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isNewAssignment ? "Assign apprentice" : "Assignment details"}
      icon={isNewAssignment ? "fa-user-plus" : "fa-circle-info"}
      size="lg"
      closeOnBackdrop={!saving}
      closeOnEscape={!saving}
      footer={
        <ModalSecondaryButton onClick={onClose}>Close</ModalSecondaryButton>
      }
    >
        <div>
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start">
              <i className="fas fa-exclamation-triangle mr-3 mt-0.5"></i>
              <div className="text-sm">{error}</div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="bg-ice-white border border-pale-dawn rounded-xl p-4">
              <div className="text-xs text-gray-500 mb-1">Apprentice</div>
              <div className="font-semibold text-grey-dusk">
                {assignment.studentName}
              </div>
            </div>

            <div className="bg-ice-white border border-pale-dawn rounded-xl p-4">
              <div className="text-xs text-gray-500 mb-1">Instructor</div>
              <div className="font-semibold text-grey-dusk">
                {assignment.instructorName || "Unassigned"}
              </div>
              {assignment.department && (
                <div className="text-xs text-slate-500 mt-1">
                  {assignment.department}
                </div>
              )}
            </div>

            {!isNewAssignment && (
              <>
                <div className="bg-ice-white border border-pale-dawn rounded-xl p-4">
                  <div className="text-xs text-gray-500 mb-1">Start</div>
                  <div className="font-semibold text-grey-dusk">
                    {format(parseISO(assignment.startAt), "dd.MM.yyyy")}
                  </div>
                </div>

                <div className="bg-ice-white border border-pale-dawn rounded-xl p-4">
                  <div className="text-xs text-gray-500 mb-1">End</div>
                  <div className="font-semibold text-grey-dusk">
                    {assignment.endAt
                      ? format(parseISO(assignment.endAt), "dd.MM.yyyy")
                      : assignment.expirationDate
                      ? format(parseISO(assignment.expirationDate), "dd.MM.yyyy")
                      : "End not defined"}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="mt-6 border-t border-gray-200 pt-5">
            <h4 className="font-bold text-grey-dusk flex items-center mb-3">
              <i className="fas fa-sliders mr-2 text-sunrise-red"></i>
              Actions
            </h4>

            {!isReassigning ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-grey-dusk mb-2">
                    Planned end date
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      className="input-brand w-full"
                      value={expirationDate || ""}
                      onChange={(e) => setExpirationDate(e.target.value)}
                      disabled={saving}
                    />
                    <button
                      onClick={handleUpdateExpiration}
                      className="flex-1 px-6 py-3 rounded-lg bg-red-600 hover:bg-red-700 text-white 
               transition-colors text-sm font-semibold flex items-center justify-center"
                      disabled={saving}
                      title="Save"
                    >
                      <i
                        className={`fas fa-save mr-2 ${saving ? "fa-spin fa-circle-notch" : ""}`}
                      ></i>
                      Save
                    </button>
                  </div>
                </div>

                <button
                  className="w-full border border-red-600 text-red-600 hover:bg-red-50 rounded-lg px-6 py-3 text-sm font-semibold flex items-center justify-center transition-colors"
                  onClick={() => setIsReassigning(true)}
                  disabled={saving}
                >
                  <i className="fas fa-right-left mr-2"></i>
                  Reassign apprentice
                </button>
              </div>
            ) : (
                <div className="bg-ice-white border border-pale-dawn rounded-xl p-4 space-y-3">
                    <div className="text-sm font-semibold text-grey-dusk flex items-center">
                    <i className={`fas ${isNewAssignment ? 'fa-plus' : 'fa-user-check'} mr-2 text-sunrise-red`}></i>
                    {isNewAssignment ? "New assignment" : "Transfer assignment"}
                    </div>

                    <div>
                    <label className="block text-xs font-medium text-grey-dusk mb-2">
                        {isNewAssignment ? "Select Instructor" : "New instructor"}
                    </label>
                    {loadingInstructors ? (
                        <div className="text-sm text-gray-500">
                        Loading instructors...
                        </div>
                    ) : (
                        <select
                        className="input-brand w-full"
                        value={newInstructorId}
                        onChange={(e) => setNewInstructorId(e.target.value)}
                        disabled={saving}
                        >
                        <option value="">Select an instructor</option>
                        <option value="UNASSIGN" className="font-semibold text-red-600">⚠ No Instructor (Unassign)</option>
                        {instructors.map((inst) => (
                            <option key={inst.id} value={inst.id}>
                            {inst.firstName} {inst.lastName }
                             ({inst.practiceInstructorDepartment || ""})
                            </option>
                        ))}
                        </select>
                    )}
                    {instructors.length === 0 && !loadingInstructors && (
                        <div className="text-xs text-orange-600 mt-1">
                        No matching instructors found.
                        </div>
                    )}
                    </div>

                    <div>
                    <label className="block text-xs font-medium text-grey-dusk mb-2">
                        Start date
                    </label>
                    <input
                        type="date"
                        className="input-brand w-full"
                        value={reassignDate}
                        onChange={(e) => setReassignDate(e.target.value)}
                        disabled={saving}
                    />
                    </div>
                
                {/* Optional end date for new assignments too */}
                <div>
                     <label className="block text-xs font-medium text-grey-dusk mb-2">
                        End Date (Optional)
                    </label>
                    <input
                        type="date"
                        className="input-brand w-full"
                        value={expirationDate || ''}
                        onChange={(e) => setExpirationDate(e.target.value)}
                        disabled={saving}
                    />
                </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    {!isNewAssignment && (
                        <button
                            onClick={() => setIsReassigning(false)}
                            className="flex-1 btn-brand-outline text-sm"
                            disabled={saving}
                        >
                            Cancel
                        </button>
                    )}
                    <button
                        onClick={handleReassign}
                        className="flex-1 px-5 py-2.5 rounded-lg bg-sunrise-red hover:bg-sunrise-600 text-white transition-colors text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                        disabled={saving || !newInstructorId}
                        title={!newInstructorId ? "Enter instructor ID" : "Confirm"}
                    >
                        <i className="fas fa-check"></i>
                        Confirm
                    </button>
                    </div>
              </div>
            )}
          </div>

        </div>
    </Modal>
  );
};

export default ExpertAssignmentView;
